import { blobToBytes } from '../../core/blob-bytes';
import type { ConvertContext, Converter } from '../../core/converter';
import { EngineFailure } from '../../core/errors';
import { canonicalExtension, buildOutputName } from '../../core/filename';
import { extensionOf } from '../../core/file-detect';
import { documentFidelity } from '../../core/fidelity';
import { LOCAL_CONVERSION_COST } from '../../core/pricing';
import type { ConversionResult, ConverterCapabilities, ConvertOptions, FileMetadata } from '../../core/types';
import { hasUnbalancedFence, verifyDocumentOutput } from '../../core/verify';
import { decodeTextFile, markdownToPlainText, normalizeNewlines, withTextNormalizationNotes } from './text-codec';

export const TEXT_SOURCE_FORMATS = ['txt', 'md', 'markdown'];
export const TEXT_TARGET_FORMATS = ['txt', 'md'];

const MIME_BY_FORMAT: Record<string, string> = {
  txt: 'text/plain',
  md: 'text/markdown',
  markdown: 'text/markdown',
};

function throwIfAborted(context?: ConvertContext): void {
  if (context?.signal?.aborted) {
    const error = new Error('任务已取消');
    error.name = 'AbortError';
    throw error;
  }
}

/** 纯文本与 Markdown 互转。 */
export class TextDocumentConverter implements Converter {
  capabilities(): ConverterCapabilities {
    return {
      id: 'document.text',
      category: 'document',
      sourceFormats: [...TEXT_SOURCE_FORMATS],
      targetFormats: [...TEXT_TARGET_FORMATS],
      local: true,
      cloud: false,
      defaultFidelity: 'L1',
      mode: 'local',
      requiresUpload: false,
      estimatedCost: LOCAL_CONVERSION_COST,
    };
  }

  canConvert(source: FileMetadata, targetExtension: string): boolean {
    const sourceExtension = canonicalExtension(source.type.extension);
    const target = canonicalExtension(targetExtension);
    return TEXT_SOURCE_FORMATS.includes(sourceExtension) && TEXT_TARGET_FORMATS.includes(target);
  }

  async inspect(file: File): Promise<FileMetadata> {
    // 文本不做尺寸字段填充：width/height 属于图片语义，避免 UI 误读。
    const bytes = await blobToBytes(file);
    decodeTextFile(bytes);
    return {
      name: file.name,
      size: file.size,
      type: { extension: extensionOf(file.name), mime: file.type || 'text/plain', category: 'document' },
    };
  }

  async convert(file: File, options: ConvertOptions = {}, context?: ConvertContext): Promise<ConversionResult> {
    const target = canonicalExtension(String(options.targetFormat ?? 'md'));
    if (!TEXT_TARGET_FORMATS.includes(target)) {
      throw new EngineFailure('UNSUPPORTED_CONVERSION', `文本引擎不支持输出 ${target} 格式`);
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 15, stage: '读取文本' });
    const bytes = await blobToBytes(file);
    const { text, encoding } = decodeTextFile(bytes);
    const source = canonicalExtension(extensionOf(file.name));
    const sourceExtension = TEXT_SOURCE_FORMATS.includes(source) ? source : 'txt';

    throwIfAborted(context);
    context?.onProgress?.({ progress: 45, stage: '转换' });
    const normalized = normalizeNewlines(text);
    const output = sourceExtension === 'txt' && target === 'md' ? `${normalized.trimEnd()}\n` : markdownToPlainText(normalized);

    if (!output.trim()) {
      throw new EngineFailure('MALFORMED_FILE', '源文件没有可转换的文本内容');
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 75, stage: '校验' });
    const verification = verifyDocumentOutput({
      parsed: output,
      assertions: [
        { label: '产物非空', ok: output.trim().length > 0 },
        {
          label: target === 'md' ? '内容与源文本一致' : '已剥离 Markdown 标记',
          ok: target === 'md' ? output.includes(normalized.trim().slice(0, 20)) : !/^\s{0,3}#{1,6}\s/m.test(output),
        },
        // 提示性：源码/正文里本就可能写着奇数个 ```，不该因此拦下产物
        { label: '无未闭合代码围栏', ok: !hasUnbalancedFence(output), advisory: true },
      ],
    });

    const baseFidelity = documentFidelity({ sourceExtension, targetExtension: target });
    const notes: string[] = [];
    if (encoding !== 'utf-8') notes.push(`源文件编码为 ${encoding}，输出统一为 UTF-8`);
    if (/\r/.test(text)) notes.push('换行符已统一为 LF');
    const fidelity = withTextNormalizationNotes(baseFidelity, notes);

    const mime = MIME_BY_FORMAT[target] ?? 'text/plain';
    const fileName = buildOutputName(file.name, target);
    return {
      blob: new Blob([output], { type: `${mime};charset=utf-8` }),
      fileName,
      metadata: {
        name: fileName,
        size: new TextEncoder().encode(output).length,
        type: { extension: target, mime, category: 'document' },
      },
      fidelity,
      verificationPassed: verification.passed,
      verificationDetails: verification.details,
    };
  }
}
